<template>
  <div class="container">
    <UrlParams v-model="urlParams" @params="(params) => (urlParams = params)" />
    <!-- Sector selection -->
    <p><strong>Choose a sector</strong></p>
    <div class="tech-controls">
      <div
        v-for="topLevel in allSectors.filter((sector) => sector.depth === 0)"
        :key="topLevel.sector"
        class="sector-tree__container"
      >
        <div
          :class="`sector-tree__child sector-tree__top-item item-selectable ${
            selectedSector === topLevel.sector ? 'item-bold' : ''
          }`"
          @click="
            () =>
              selectSector(
                selectedSector === topLevel.sector ? null : topLevel.sector
              )
          "
        >
          {{ topLevel.sector }}
        </div>
        <div class="sector-tree">
          <div
            v-for="child in allSectors.filter(
              (sector) => sector.parent === topLevel.sector
            )"
            :key="child.sector"
            :class="`sector-tree__child`"
            @click="
              () =>
                selectSector(
                  selectedSector === child.sector ? null : child.sector
                )
            "
          >
            <span
              v-for="(indent, index) in [...Array(child.depth)]"
              :key="index"
              >-</span
            >&nbsp;
            <span
              :class="`item-selectable ${
                selectedSector === child.sector ? 'item-bold' : ''
              }`"
              >{{ child.sector }}</span
            >
          </div>
        </div>
      </div>
    </div>
    <p class="f-ui-1">
      Filter
      <strong class="f-ui-1-bold">
        {{ selectedSector ? selectedSector.toLowerCase() : "all" }}</strong
      >&nbsp;technologies&nbsp;
      <small
        v-show="!showAdvanced"
        @click="forceShowAdvanced"
        class="item-selectable"
        >Show advanced filters</small
      >
    </p>

    <!-- Initial filters -->
    <ControlGroup>
      <Dropdown
        label="Cross-cutting theme"
        :options="[
          { label: 'All', value: null },
          ...availableCCTags.map((tag) => ({ label: tag, value: tag })),
        ]"
        v-model="selectedCCTag"
      />
      <Dropdown
        label="Key countries"
        :options="[
          { label: 'All', value: null },
          ...availableCountries.map((country) => ({
            label: country,
            value: country,
          })),
        ]"
        v-model="country"
      />

      <StyledInput
        label="Search"
        title="Enter search terms to find all matching technologies"
        v-model="search"
      />
    </ControlGroup>
    <!-- Advanced filters  -->
    <hr />
    <ControlGroup v-show="showAdvanced">
      <ControlGroup>
        <Dropdown
          label="Step in value chain"
          :options="[
            { label: 'All', value: null },
            ...availableVCSteps.map((step) => ({
              label: step,
              value: step,
            })),
          ]"
          v-model="selectedVCStep"
        />
        <Dropdown
          label="Technology readiness level"
          :options="[
            { label: 'Any', value: null },
            { label: 'Concept (TRL 1-3)', value: '1-3' },
            { label: 'Small prototype (TRL 4)', value: '4' },
            { label: 'Large prototype (TRL 5-6)', value: '5-6' },
            { label: 'Demonstration (TRL 7-8)', value: '7-8' },
            { label: 'Early adoption (TRL 9-10)', value: '9-10' },
            { label: 'Mature (TRL 11)', value: '11' },
          ]"
          v-model="trlRange"
        />
        <Dropdown
          label="Importance for net zero emissions"
          :options="[
            { label: 'All', value: null },
            { label: 'Moderate', value: 'Moderate' },
            { label: 'High', value: 'High' },
            { label: 'Very high', value: 'Very high' },
          ]"
          v-model="netZeroPriority"
        />
      </ControlGroup>
      <Multiselect
        label="Technology areas"
        :options="
          availableTechs.map((tech) => ({
            label: tech,
            value: tech,
            level: techLevel(tech),
            highlighted: techLevel(tech) === 0,
          }))
        "
        v-model="selectedTechs"
      />
    </ControlGroup>
    <ControlGroup v-show="!noFiltersApplied">
      <StyledButton small :onClick="() => resetFilters(true)"
        >Clear all filters</StyledButton
      >
    </ControlGroup>
    <!--
    <div class="container">
      <Dropdown
        small
        label="Show up to"
        :options="[
          { label: '10 technologies', value: 10 },
          { label: '25 technologies', value: 25 },
          { label: '50 technologies', value: 50 },
          { label: 'All technologies', value: null },
        ]"
        v-model="showItems"
      />
      
    </div>
    -->

    <div class="tech-controls">
      <div>
        Showing&nbsp;
        <span>{{ filteredItems.length }}</span>
        &nbsp;
        <strong
          v-if="selectedTechs"
          @click="() => selectTech(null)"
          title="Clear technology filter"
          class="item-selectable"
          >{{ selectedTechs.join(" ,") }}</strong
        >
        <span v-if="selectedVCStep">
          &nbsp;<strong
            @click="() => selectVCStep(null)"
            title="Clear value chain step filter"
            class="item-selectable"
            >{{ selectedVCStep }}</strong
          >
        </span>
        technologies
        <span v-if="search">
          containing the phrase <strong>"{{ search }}"</strong>
        </span>
        <span v-if="trlRange">
          with a TRL of
          <strong
            @click="clearTRL"
            title="Clear TRL filter"
            class="item-selectable"
            >{{ trlRange }}</strong
          >
        </span>
        <span v-if="netZeroPriority">
          with
          <strong
            @click="() => selectNetZero(null)"
            title="Clear net zero filter"
            class="item-selectable"
            >{{ netZeroPriority }}</strong
          >
          importance for net-zero emissions
        </span>
        <span v-if="selectedSector">
          in the
          <strong
            @click="() => selectSector(null, true)"
            title="Clear sector filter"
            class="item-selectable"
            >{{ selectedSector }}</strong
          >
          sector
        </span>
        <span v-if="selectedCCTag">
          related to
          <strong
            @click="() => selectCCTag(null)"
            title="Clear cross-cutting tag filter"
            class="item-selectable"
            >{{ selectedCCTag }}</strong
          >
        </span>
        <span v-if="country">
          where
          <strong
            @click="() => selectCountry(null)"
            title="Clear country filter"
            class="item-selectable"
            >{{ country }}</strong
          >
          is a key country
        </span>
        &nbsp;
      </div>
    </div>

    <div class="container">
      <p v-if="shownItems.length === 0">
        Please select an option from the filters above to see matching
        technologies.
      </p>

      <table class="tech-table" v-if="shownItems.length > 0">
        <thead>
          <tr>
            <th
              class="tech-table__heading--TRL"
              @click="() => sort('TRL')"
              title="Sort by TRL"
            >
              <Sorter
                sort-by="TRL"
                :sort-direction="sortDirection"
                :sort="sort"
                :active="sortBy === 'TRL'"
                title="TRL"
              >
                <span class="hide-responsive">
                  Readiness level (TRL)&nbsp;<span
                    @mouseover="toggleTRLExplainer"
                    @mouseout="toggleTRLExplainer"
                    class="TRL-explainer__expand"
                    title="Show explanation of TRLs"
                    >?</span
                  >
                  <span class="show-responsive"> TRL </span>
                </span>
              </Sorter>
            </th>
            <th
              class="tech-table__heading--sector"
              @click="() => sort('sectors')"
              title="Sort by sector"
            >
              <Sorter
                sort-by="sectors"
                :sort-direction="sortDirection"
                :sort="sort"
                :active="sortBy === 'sectors'"
                title="sector"
              >
                Sector
              </Sorter>
            </th>
            <th
              class="tech-table__heading--technology"
              @click="() => sort('techs')"
              title="Sort by technology"
            >
              <Sorter
                sort-by="techs"
                :sort-direction="sortDirection"
                :sort="sort"
                :active="sortBy === 'techs'"
                title="technology"
              >
                Technology
              </Sorter>
            </th>
            <th
              class="tech-table__heading--vcStep"
              @click="() => sort('valueChainStep')"
              title="Sort by value chain step"
            >
              <Sorter
                sort-by="valueChainStep"
                :sort-direction="sortDirection"
                :sort="sort"
                :active="sortBy === 'valueChainStep'"
                title="value chain step"
              >
                Step in value chain
              </Sorter>
            </th>
            <th
              class="tech-table__heading--netZero"
              @click="() => sort('netZeroPriority')"
              title="Sort by net zero priority"
            >
              <Sorter
                sort-by="netZeroPriority"
                :sort-direction="sortDirection"
                :sort="sort"
                :active="sortBy === 'netZeroPriority'"
                title="net zero importance"
              >
                Importance for net-zero emissions
              </Sorter>
            </th>
            <th></th>
          </tr>
        </thead>
        <transition-group
          name="tech-table-item"
          tag="tbody"
          appear
          mode="out-in"
        >
          <template v-for="item in shownItems">
            <tr :key="`${item.hash}-summary`">
              <td
                class="tech-table__cell tech-table__cell--centered"
                :key="`${item.hash}-trl`"
              >
                <span
                  :class="`tech-table__trl tech-table__trl--${
                    trlClass(item.TRL)[0]
                  }`"
                  @mouseover="toggleTRLExplainer"
                  @mouseout="toggleTRLExplainer"
                  @click="toggleTRLExplainer"
                >
                  {{ item.TRL }}
                </span>
              </td>
              <td class="tech-table__cell" :key="`${item.hash}-sectors`">
                <span v-for="(sector, index) in item.sectors" :key="sector">
                  <span
                    v-if="selectedSector === sector"
                    class="item-selectable item-selectable--subtle item-bold"
                    @click="() => removeSector(sector)"
                    title="Remove this sector from your filters"
                  >
                    {{ sector }}
                  </span>
                  <span
                    v-else
                    class="item-selectable item-selectable--subtle"
                    @click="() => selectSector(sector, true)"
                    title="Add this sector to your filters"
                  >
                    {{ sector }}
                  </span>
                  <span v-if="index < item.sectors.length - 1"> &gt; </span>
                </span>
              </td>
              <td class="tech-table__cell" :key="`${item.hash}-techs`">
                <span v-for="(tech, index) in item.techs" :key="tech">
                  <span
                    v-if="selectedTechs.includes(tech)"
                    class="item-selectable item-selectable--subtle item-bold"
                    @click="() => removeTech(tech)"
                    title="Remove this technology from your filters"
                  >
                    {{ tech }}
                  </span>
                  <span
                    v-else
                    class="item-selectable item-selectable--subtle"
                    @click="() => selectTech(tech)"
                    title="Add this technology to your filters"
                  >
                    {{ tech }}
                  </span>
                  <span v-if="index < item.techs.length - 1"> &gt; </span>
                </span>
              </td>
              <td
                class="
                  tech-table__cell
                  tech-table__cell--centered
                  tech-table__cell--hide-responsive
                "
                :key="`${item.hash}-vcStep`"
              >
                <span
                  v-if="selectedVCStep === item.valueChainStep"
                  class="item-selectable item-selectable--subtle item-bold"
                  @click="() => selectVCStep(null)"
                  title="Remove value chain step from your filters"
                >
                  {{ item.valueChainStep }}
                </span>
                <span
                  v-else
                  class="item-selectable item-selectable--subtle"
                  @click="() => selectVCStep(item.valueChainStep)"
                  title="Add value chain step to your filters"
                >
                  {{ item.valueChainStep }}
                </span>
              </td>
              <td
                class="
                  tech-table__cell
                  tech-table__cell--centered
                  tech-table__cell--hide-responsive
                "
                :key="`${item.hash}-netZero`"
              >
                <span
                  v-if="netZeroPriority === item.netZeroPriority"
                  class="item-selectable item-selectable--subtle item-bold"
                  @click="() => selectNetZero(null)"
                  title="Remove net zero priority from your filters"
                >
                  {{ item.netZeroPriority }}
                </span>
                <span
                  v-else
                  class="item-selectable item-selectable--subtle"
                  @click="() => selectNetZero(item.netZeroPriority)"
                  title="Add net zero priority to your filters"
                >
                  {{ item.netZeroPriority }}
                </span>
              </td>
              <td class="tech-table__cell" :key="`${item.hash}-showDetails`">
                <span
                  class="item-selectable"
                  @click="
                    viewItem(
                      showDetails === itemHash(item) ? null : itemHash(item)
                    )
                  "
                  >{{
                    showDetails === itemHash(item) ? "Hide" : "Details"
                  }}</span
                >
              </td>
            </tr>
            <!-- Details -->
            <tr
              :key="`${item.hash}-detail`"
              :class="`tech-table__details ${
                showDetails === itemHash(item)
                  ? 'tech-table__details--visible'
                  : ''
              }`"
            >
              <td
                colspan="6"
                :key="`${item.hash}-detail-description`"
                class="tech-table__details-description"
              >
                <div class="tech-table__details-description--inside">
                  <p
                    v-html="$options.filters.clickableLinks(item.description)"
                  />
                  <p v-if="item.ccTags.length > 0">
                    <strong>Cross-cutting themes:</strong>&nbsp;
                    <span v-for="(tag, index) in item.ccTags" :key="tag"
                      ><span
                        :class="`item-selectable ${
                          selectedCCTag === tag ? 'item-bold' : ''
                        }`"
                        @click="
                          () => selectCCTag(selectedCCTag === tag ? null : tag)
                        "
                        :title="`${
                          selectedCCTag === tag
                            ? 'Clear theme filter'
                            : 'Add this theme to your filters'
                        }`"
                        >{{ tag }}</span
                      ><span v-if="index < item.ccTags.length - 1">, </span>
                    </span>
                  </p>
                  <p v-if="item.leadingCountries.length > 0">
                    <strong>Key countries:</strong>&nbsp;
                    <span
                      v-for="(leadingCountry, index) in item.leadingCountries"
                      :key="leadingCountry"
                      ><span
                        :class="`item-selectable ${
                          country === leadingCountry ? 'item-bold' : ''
                        }`"
                        @click="
                          () =>
                            selectCountry(
                              country === leadingCountry ? null : leadingCountry
                            )
                        "
                        :title="`${
                          country === leadingCountry
                            ? 'Clear country filter'
                            : 'Add this country to your filters'
                        }`"
                        >{{ leadingCountry }}</span
                      ><span v-if="index < item.leadingCountries.length - 1"
                        >,
                      </span>
                    </span>
                  </p>
                  <div v-if="item.prototypes">
                    <p>
                      <strong>Key initiatives:</strong>
                    </p>
                    <p
                      v-html="
                        $options.filters.clickableLinks(
                          item.prototypes.split('*')[0]
                        )
                      "
                    ></p>
                    <ul>
                      <li
                        v-for="(target, index) in item.prototypes
                          .split('*')
                          .slice(1)"
                        :key="`initiative${index}`"
                        v-html="$options.filters.clickableLinks(target)"
                      />
                    </ul>
                  </div>
                  <div v-if="item.deploymentTargets">
                    <p>
                      <strong v-if="trlClass(item.TRL) < 9"
                        >Announced development targets:</strong
                      >
                      <strong v-else>Deployment targets:</strong>
                    </p>
                    <p
                      v-html="
                        $options.filters.clickableLinks(
                          item.deploymentTargets.split('*')[0]
                        )
                      "
                    ></p>
                    <ul>
                      <li
                        v-for="(target, index) in item.deploymentTargets
                          .split('*')
                          .slice(1)"
                        :key="`target${index}`"
                        v-html="$options.filters.clickableLinks(target)"
                      />
                    </ul>
                  </div>
                  <div v-if="item.costTargets">
                    <p><strong>Announced cost reduction targets:</strong></p>
                    <ul>
                      <li
                        v-for="(target, index) in item.costTargets.split('*')"
                        :key="`costTarget${index}`"
                        v-html="$options.filters.clickableLinks(target)"
                      />
                    </ul>
                  </div>
                  <div
                    v-if="
                      isDemo(item.TRL) &&
                      item.sectors.some((sector) =>
                        demoSubsectors.includes(sector)
                      )
                    "
                  >
                    Explore demonstration projects related to this sector in the
                    <a
                      :href="`http://www.iea.org/data-and-statistics/data-tools/clean-energy-demonstration-projects-database?subsector=${item.sectors.find(
                        (sector) => demoSubsectors.includes(sector)
                      )}`"
                      >Clean Energy Demonstration Projects database</a
                    >.
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </transition-group>
      </table>

      <p v-if="showItems && filteredItems.length > showItems">
        <span class="item-selectable" @click="setShowItems(null)"
          >Show all</span
        >
      </p>

      <!-- TRL explainer box -->
      <TRLExplainer :active="showTRLExplainer" :close="toggleTRLExplainer" />
    </div>
  </div>
</template>
<script>
const demoSubsectors = [
  "Buildings construction and renovation",
  "Heating and cooling",
  "Cooking",
  "Lighting",
  "Systems integration",
  "Power",
  "Heat",
  "Biofuels",
  "Synthetic hydrocarbon fuels",
  "Refining",
  "Hydrogen",
  "Road",
  "Rail",
  "Shipping",
  "Aviation",
  "Direct air capture",
  "CO2 direct utilisation",
  "CO2 transport",
  "CO2 storage",
  "Chemicals and plastics",
  "Battery recycling",
  "Cement and concrete",
  "Pulp and paper",
  "Aluminium",
  "Iron and steel",
  "Cross-cutting",
];
import items from "./assets/techs.json";
import Sorter from "./components/Sorter.vue";
import TRLExplainer from "./components/TRLExplainer.vue";
import {
  ControlGroup,
  Dropdown,
  Multiselect,
  StyledButton,
  StyledInput,
  UrlParams,
} from "@international-energy-agency/vue-ui";
window.items = items;
export default {
  name: "app",
  template: "#vue-template",
  components: {
    ControlGroup,
    Dropdown,
    Multiselect,
    StyledButton,
    StyledInput,
    UrlParams,
    Sorter,
    TRLExplainer,
  },
  data() {
    return {
      items,
      allSectors: items.reduce((sectors, item) => {
        item.sectors.forEach((itemSector, index, itemSectors) => {
          const parent = index === 0 ? null : itemSectors[index - 1];
          if (
            !sectors.find(
              (sector) =>
                sector.sector === itemSector && sector.parent === parent
            )
          ) {
            sectors.push({
              sector: itemSector,
              depth: index,
              parent,
            });
          }
        });
        return sectors;
      }, []),
      allTechs: items.reduce((techs, item) => {
        item.techs.forEach((itemTech, index, itemTechs) => {
          if (!techs.find((tech) => tech.tech === itemTech)) {
            techs.push({
              tech: itemTech,
              depth: index,
              parent: index === 0 ? null : itemTechs[index - 1],
            });
          }
        });
        return techs;
      }, []),
      selectedCCTag: null,
      selectedVCStep: null,
      selectedSector: null,
      selectedTechs: [],
      country: null,
      trlRange: null,
      netZeroPriority: null,
      search: "",
      showDetails: null,
      showTRLExplainer: false,
      showAdvancedManual: false,
      sortBy: "netZeroPriority",
      sortDirection: "dsc",
      showItems: 50,
      demoSubsectors,
      validParams: [
        "selectedCCTag",
        "selectedVCStep",
        "selectedSector",
        //"selectedTechs",
        "trlRange",
        "netZeroPriority",
        "country",
        "search",
      ],
    };
  },
  filters: {
    sector(sector) {
      return `${[...Array(sector.depth)].map(() => "-").join("")} ${
        sector.sector
      }`;
    },
    tech(tech) {
      return `${[...Array(tech.depth)].map(() => "-").join("")} ${tech.tech}`;
    },
    clickableLinks(rawText) {
      // remove newlines
      let text = rawText.split("\n").join(" ");
      const regexp = /\b(http)(?:[^\s,.!?]|[,.!?](?!\s))+/g;
      const matches = text.match(regexp);
      if (!matches) return text;
      matches.forEach((match) => {
        text = text.replace(match, `<a href="${match}">Source</a>`);
      });
      return text;
    },
  },
  watch: {
    shownItems(from, to) {
      // Hide shown item if it's removed from the listing
      if (
        this.showDetails &&
        !to.some((item) => item.hash === this.showDetails)
      ) {
        this.showDetails = null;
      }
    },
  },
  methods: {
    forceShowAdvanced() {
      this.showAdvancedManual = true;
    },
    resetFilters(all) {
      this.selectedTechs = [];
      this.country = null;
      this.trlRange = null;
      this.netZeroPriority = null;
      this.selectedCCTag = null;
      this.selectedVCStep = null;
      this.search = null;
      if (all) {
        this.selectedSector = null;
      }
    },
    clearTRL() {
      this.trlRange = null;
    },
    sort(sortBy, direction) {
      this.sortBy = sortBy;
      this.sortDirection = direction || this.sortDirection;
    },
    alphabetize(list, valueAttribute) {
      return list.sort((a, b) =>
        a[valueAttribute] > b[valueAttribute] ? 1 : -1
      );
    },
    itemHash(item) {
      return (
        item.sectors.join("").split(" ").join("") +
        item.techs.join("").split(" ").join("")
      );
    },
    toggleTRLExplainer() {
      this.showTRLExplainer = !this.showTRLExplainer;
    },
    sectorLevel(sector) {
      const sectorDetails = this.allSectors.find(
        (sectorDetail) => sectorDetail.sector === sector
      );
      return sectorDetails.depth;
    },
    techLevel(tech) {
      const techDetails = this.allTechs.find(
        (techDetail) => techDetail.tech === tech
      );
      if (!techDetails) return false;
      return techDetails.depth;
    },
    setShowItems(num) {
      this.showItems = num;
    },
    showItem(item) {
      const {
        selectedCCTag,
        selectedVCStep,
        selectedSector,
        selectedTechs,
        trlRange,
        netZeroPriority,
        country,
        search,
      } = this;
      let filtered = false;
      if (selectedCCTag) {
        filtered = !item.ccTags.includes(selectedCCTag) || filtered;
      }
      if (selectedVCStep) {
        filtered = item.valueChainStep !== selectedVCStep || filtered;
      }
      if (selectedSector) {
        filtered =
          item.sectors.length === 0 ||
          !item.sectors.includes(selectedSector) ||
          filtered;
      }
      if (selectedTechs.length > 0) {
        filtered =
          item.techs.length === 0 ||
          !selectedTechs.every((tech) => item.techs.includes(tech)) ||
          filtered;
      }
      if (trlRange) {
        const showTRL = trlRange.split("-").map((trl) => parseInt(trl));
        if (showTRL.length === 1) showTRL.push(showTRL[0]);
        const itemTRL = this.trlClass(item.TRL);
        const minTRL = itemTRL[0];
        const maxTRL = itemTRL.length > 1 ? itemTRL[1] : minTRL;
        filtered = minTRL < showTRL[0] || maxTRL > showTRL[1] || filtered;
      }
      if (netZeroPriority) {
        filtered = item.netZeroPriority !== netZeroPriority || filtered;
      }
      if (country) {
        filtered = !item.leadingCountries.includes(country) || filtered;
      }
      if (search) {
        filtered =
          !Object.values(item).some((field) =>
            field.toString().toLowerCase().includes(search)
          ) || filtered;
      }
      return !filtered;
    },
    // Optional add to filter param adds the sector selection to the current filters
    selectSector(sector, addToFilter) {
      this.selectedSector = sector;
      if (!addToFilter) {
        this.resetFilters();
      }
    },
    removeSector() {
      this.selectedSector = null;
    },
    selectTech(tech) {
      // if null, clear all values
      if (!tech) {
        this.selectedTechs = [];
      } else {
        // otherwise try to add it to the list, filtering deeper and equal items
        this.selectedTechs = [
          ...this.selectedTechs.filter((selectedTech) => {
            const selectedTechDepth = this.allTechs.find(
              (allTech) => allTech.tech === tech
            ).depth;
            const techDetails = this.allTechs.find(
              (tech) => tech.tech === selectedTech
            );
            return techDetails.depth < selectedTechDepth;
          }),
          tech,
        ];
      }
      // set the select to the new value
      this.$refs.selectTech.value = tech;
    },
    removeTech(tech) {
      this.selectedTechs = this.selectedTechs.slice(
        0,
        this.selectedTechs.indexOf(tech)
      );
      // set the select to the new lowest value
      this.$refs.selectTech.value =
        this.selectedTechs[this.selectedTechs.length - 1] || null;
    },
    selectCCTag(tag) {
      this.selectedCCTag = tag;
    },
    selectVCStep(step) {
      this.selectedVCStep = step;
    },
    selectNetZero(netZero) {
      this.netZeroPriority = netZero;
    },
    selectCountry(country) {
      this.country = country;
    },
    isDemo(rawTrl) {
      return this.trlClass(rawTrl).some((TRL) => TRL === 7 || TRL === 8);
    },
    trlClass(rawTrl) {
      if (!rawTrl) return [];
      const txt = rawTrl.toString();
      const regex = /[0-9]+/g;
      const matches = [];
      let match = regex.exec(txt);
      while (match !== null) {
        matches.push(parseInt(match));
        match = regex.exec(txt);
      }
      return matches;
    },
    viewItem(id) {
      this.showDetails = id;
    },
  },
  computed: {
    urlParams: {
      get: function () {
        return this.validParams.reduce(
          (urlParams, param) => ({ ...urlParams, [param]: this[param] }),
          {}
        );
      },
      set: function (newValue) {
        Object.keys(newValue)
          .filter((param) => this.validParams.includes(param))
          .forEach((param) => {
            this[param] = Array.isArray(this[param])
              ? newValue[param].split(",")
              : newValue[param];
          });
      },
    },
    noFiltersApplied() {
      return (
        this.selectedSector === null &&
        this.selectedTechs.length === 0 &&
        this.country === null &&
        this.trlRange === null &&
        this.netZeroPriority === null &&
        this.selectedCCTag === null &&
        this.selectedVCStep === null
      );
    },
    availableCountries() {
      const countries = [];
      const items =
        this.filteredItems.length > 0 ? this.filteredItems : this.items;
      items.map((item) => {
        item.leadingCountries.forEach((country) => {
          if (countries.indexOf(country) === -1) countries.push(country);
        });
      });
      return countries.sort();
    },
    availableCCTags() {
      const { items, filteredItems } = this;
      const useItems = filteredItems.length > 0 ? filteredItems : items;
      return useItems.reduce((ccTags, item) => {
        item.ccTags.forEach((itemTag) => {
          if (!ccTags.includes(itemTag)) {
            ccTags.push(itemTag);
          }
        });
        return ccTags.sort();
      }, []);
    },
    availableVCSteps() {
      const { items, filteredItems } = this;
      const useItems = filteredItems.length > 0 ? filteredItems : items;
      return useItems.reduce((vcSteps, item) => {
        if (item.valueChainStep && !vcSteps.includes(item.valueChainStep)) {
          vcSteps.push(item.valueChainStep);
        }
        return vcSteps.sort();
      }, []);
    },
    availableTechs() {
      const { noFiltersApplied, items, filteredItems, selectedTechs } = this;
      const useItems = noFiltersApplied ? items : filteredItems;
      // if no sectors are selected, show only top level techs
      if (selectedTechs.length === 0) {
        return useItems.reduce((techs, item) => {
          if (!techs.includes(item.techs[0])) {
            techs.push(item.techs[0]);
          }
          /*
          if (item.techs[1] && !techs.includes(item.techs[1])) {
            techs.push(item.techs[1]);
          }
          */
          return techs;
        }, []);
      }
      // Otherwise add all the available techs
      return useItems.reduce((techs, item) => {
        item.techs.forEach((itemTech) => {
          if (!techs.includes(itemTech)) {
            techs.push(itemTech);
          }
        });
        return techs;
      }, []);
    },
    filteredItems() {
      const { sortBy, sortDirection } = this;
      const netZeroSort = [null, "Low", "Moderate", "High", "Very high"];
      const vcStepSort = [
        null,
        "Prodution",
        "Generation",
        "Infrastructure",
        "Transport",
        "Storage",
        "End-use and operations",
        "End-of-life",
      ];
      return items
        .filter(this.showItem)
        .map((item) => {
          item.hash = this.itemHash(item);
          return item;
        })
        .sort((a, b) => {
          let aVal = a[sortBy];
          let bVal = b[sortBy];
          if (sortBy === "TRL") {
            aVal = parseInt(this.trlClass(a.TRL));
            bVal = parseInt(this.trlClass(b.TRL));
          }
          if (sortBy === "netZeroPriority") {
            aVal = netZeroSort.indexOf(a.netZeroPriority);
            bVal = netZeroSort.indexOf(b.netZeroPriority);
          }
          if (sortBy === "valueChainStep") {
            aVal = vcStepSort.indexOf(a.valueChainStep);
            bVal = vcStepSort.indexOf(b.valueChainStep);
          }
          if (sortBy === "sectors" || sortBy === "techs") {
            aVal = a[sortBy].join("");
            bVal = b[sortBy].join("");
          }
          const order = aVal < bVal ? 1 : -1;
          return sortDirection === "dsc" ? order : -order;
        });
    },
    shownItems() {
      return this.filteredItems.slice(
        0,
        this.showItems || this.filteredItems.length
      );
    },
    forceAdvanced() {
      return (
        this.selectedTechs.length > 0 ||
        this.selectedVCStep ||
        this.minTRL ||
        this.netZeroPriority
      );
    },
    showAdvanced() {
      return this.showAdvancedManual || !this.noFiltersApplied;
    },
  },
};
</script>

<style>
.tech-controls {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 2rem;
}
.tech-controls p {
  margin-left: 1em;
}
.tech-controls__group {
  display: flex;
  padding-right: 1em;
  flex: 1 0 auto;
}
.tech-controls__control {
  width: 100%;
}
.tech-controls__control label {
  display: block;
}
.tech-controls__control select {
  width: 100%;
}
.sector-tree__container {
  flex: 1 0 0px;
  margin-left: 2em;
  margin-top: 1em;
}
.sector-tree {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  padding-left: 1em;
  max-height: 28rem;
}
.sector-tree__child,
p.sector-tree__child {
  margin: 0 1em 0.25em 0 !important;
  text-indent: -1em;
}
.sector-tree__top-item {
  font-size: 1.2em;
  margin-left: 0 !important;
  text-indent: 0;
}
.item-bold {
  font-weight: 600;
}
.item-selectable {
  text-decoration: underline;
  cursor: pointer;
}
.item-selectable--subtle {
  color: #000;
  text-decoration: none;
}
.item-selectable:hover {
  text-decoration: underline;
  color: #04f;
}
.tech-table {
  text-align: left;
  width: 100%;
}
table.tech-table {
  border: 0;
  table-layout: fixed;
}
table.tech-table th {
  position: relative;
  background: none;
  font-weight: 600;
  vertical-align: top;
  cursor: pointer;
}
.tech-table__heading--TRL {
  width: 10%;
}
.tech-table__heading--sector {
  width: 25%;
}
.tech-table__heading--technology {
  width: 25%;
}
.tech-table__heading--vcStep {
  width: 15%;
}
.tech-table__heading--netZero {
  width: 15%;
}
.tech-table__heading--details {
  width: 5%;
}
table td.tech-table__cell {
  font-size: 14px;
}
table td.tech-table__cell--centered {
  text-align: center;
}
.TRL-explainer__expand {
  display: inline-block;
  font-weight: 400;
  text-align: center;
  width: 1.25em;
  height: 1.25em;
  border-radius: 50%;
  background: #e6e6e6;
  cursor: help;
}
.tech-table__trl {
  display: block;
  margin: auto;
  padding: 0.25rem 0;
  width: 100%;
  max-width: 5rem;
  background: #e6e6e6;
  cursor: help;
}
.tech-table__trl--idea {
  background: #ff754b;
}
.tech-table__trl--prototype {
  background: #fff45a;
}
.tech-table__trl--demo {
  background: #9bf0c5;
}
.tech-table__trl--scaling {
  background: #68f394;
}
.tech-table__trl--1 {
  background: #c797c9;
}
.tech-table__trl--2 {
  background: #c9abf4;
}
.tech-table__trl--3 {
  background: #dfc3e0;
}
.tech-table__trl--4 {
  background: #ff9683;
}
.tech-table__trl--5 {
  background: #f5c24d;
}
.tech-table__trl--6 {
  background: #ffd48e;
}
.tech-table__trl--7 {
  background: #fee066;
}
.tech-table__trl--8 {
  background: #fff78c;
}
.tech-table__trl--9 {
  background: #61d291;
}
.tech-table__trl--10 {
  background: #96f7b4;
}
.tech-table__trl--11 {
  background: #80e0ff;
}
.tech-table__details {
  visibility: collapse;
}
.tech-table__details-description {
  position: relative;
  padding: 0 !important;
}
.tech-table__details-description--inside {
  margin: 0 1rem 1rem 0;
  background: #e6e6e6;
  display: block;
  box-sizing: border-box;
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  padding: 1rem 2rem;
  transition: max-height 1.5s cubic-bezier(0, 1.05, 0, 1), opacity 400ms;
}
.tech-table__details--visible {
  visibility: visible;
}
.tech-table__details--visible .tech-table__details-description--inside {
  max-height: 1000px;
  opacity: 1;
  transition: max-height 2s ease, opacity 500ms;
}

/* Advanced drawer */
.tech-controls__advanced {
  max-height: 0;
  overflow: hidden;
  border-bottom: 1px solid #e6e6e6;
  margin-bottom: 1em;
}
.tech-controls__advanced--open {
  max-height: 500px;
  transition: max-height 500ms;
}
/* Animations */
.tech-table-item {
  opacity: 1;
}
.tech-table-item-enter,
.tech-table-item-leave-to {
  opacity: 0;
  transform: translateX(-500px);
}
.tech-table-item-leave-active {
  transition: opacity 0.3s ease-in-out, transform 0.5s ease-in-out;
}
.tech-table-item-enter-active {
  transition: opacity 0.5s ease-in-out, transform 0.5s ease-in-out;
}
.tech-table-item-move {
  transition: all 0.3s;
}
/* Media queries */
.show-responsive {
  visibility: hidden;
}
@media screen and (max-width: 1025px) {
  .sector-tree {
    font-size: 14px;
  }
}
@media screen and (max-width: 729px) {
  .tech-table__heading--TRL {
    width: 10%;
  }
  .tech-table__heading--sector {
    width: 40%;
  }
  .tech-table__heading--technology {
    width: 40%;
  }
  .tech-table__heading--vcStep {
    visibility: collapse;
    width: 0;
  }
  .tech-table__heading--netZero {
    visibility: collapse;
    width: 0;
  }
  .tech-table__heading--details {
    width: 5%;
  }
  .tech-table__cell--hide-responsive {
    visibility: collapse;
  }
  .hide-responsive {
    visibility: hidden;
  }
  .show-responsive {
    visibility: visible;
  }
}
</style>
